import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Container } from "reactstrap";
import { Box, Button, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import AddIcon from "@mui/icons-material/Add";
import { isMSP } from "../../helpers/util_helper";
import { listPartnerCustomers } from "../../helpers/partnerCentral_helper";
import {
  AH_MUTED,
  AH_TEXT,
  AuditHubThemeProvider,
  auditHubPageSx,
  primaryButtonSx,
  tableShellSx,
} from "../Compliance/AuditHub/theme";
import CustomersGrid from "./CustomersGrid";

const CUSTOMER_PATH = "/partner-central/customer";

/**
 * Standalone Partner Central customers page (no Dashboard tabs).
 * Kept for add-customer / task-order return navigation.
 */
const Customer = () => {
  const history = useHistory();
  const location = useLocation();
  const allowed = isMSP();
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState("");
  const [customersVersion, setCustomersVersion] = useState(0);

  useEffect(() => {
    document.title = "Customer | Partner Central | CloudNova App";
  }, []);

  useEffect(() => {
    const notice = location.state && location.state.notice;
    if (!notice) return;
    enqueueSnackbar(notice, { variant: "success" });
    history.replace(CUSTOMER_PATH);
  }, [location.state, history]);

  useEffect(() => {
    if (!allowed) {
      history.replace("/dashboard");
    }
  }, [allowed, history]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    setCustomersLoading(true);
    setCustomersError("");
    listPartnerCustomers({ statuses: ["DRAFT", "ACTIVE"] })
      .then((items) => {
        if (!cancelled) setCustomers(items);
      })
      .catch((error) => {
        if (!cancelled) {
          setCustomers([]);
          setCustomersError(error?.message || "Unable to load customers.");
        }
      })
      .finally(() => {
        if (!cancelled) setCustomersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, location.key, customersVersion]);

  if (!allowed) {
    return null;
  }

  return (
    <AuditHubThemeProvider>
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid>
          <Box
            sx={{
              pt: 1.5,
              pb: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              ...auditHubPageSx,
            }}
          >
            <Box sx={tableShellSx}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="h1"
                    sx={{
                      m: 0,
                      fontWeight: 700,
                      fontSize: { xs: "1.125rem", md: "1.25rem" },
                      color: AH_TEXT,
                      letterSpacing: "-0.01em",
                      lineHeight: 1.3,
                    }}
                  >
                    Customer
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>
                    Downstream customer commercial terms and accounts
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={() => history.push(`${CUSTOMER_PATH}/add-customer`)}
                  sx={{ ...primaryButtonSx, flexShrink: 0 }}
                >
                  Add Customer
                </Button>
              </Box>
            </Box>

            <CustomersGrid
              customers={customers}
              loading={customersLoading}
              error={customersError}
              onRefresh={() => setCustomersVersion((value) => value + 1)}
            />
          </Box>
        </Container>
      </div>
    </AuditHubThemeProvider>
  );
};

export default Customer;
